import qs from "qs";
import api from "../services/api";

import { IPagination } from "../models/IPagination";
import { IPendingAction } from "../models/ITeam";
import { ITournament, ITournamentFormatResponse, ITournamentItem } from "../models/ITournament";
import { IGroupStandingList } from "../models/IGroup";
import { ILeagueStanding } from "../models/ILeague";
import { byTotalPoints } from "../utils/standings";
import { IAmericanoFinishResult, IAmericanoLeaderboardRow, IMexicanoSession } from "../models/IAmericano";
import { IEditMatchDTO, IMatchDetailDTO, ITeamMatch } from "../models/IMatch";

import {
  AddTournamentCategoryDTO,
  AddTournamentDTO,
  AvailableSpotsDTO,
  EditTournamentCategoryDTO,
  EditTournamentDTO,
  IMatchFilters,
  IndividualRegistrationDTO,
  IndividualRegistrationResponse,
  StageDetails,
  TournamentResponseDTO,
  UpdatePrizePaymentRequestDTO,
} from "../types/tournament";
import { IServiceResponse } from "../types/api";

export const addTournament = (tournament: AddTournamentDTO): Promise<{ data: ITournament }> =>
  api.post("Tournament", tournament);

export const editTournament = (id: string, tournament: EditTournamentDTO): Promise<{ data: TournamentResponseDTO }> =>
  api.put(`Tournament/${id}`, tournament);

export const deleteTournament = (id: string): Promise<{ data: boolean; success: boolean; message: string }> =>
  api.delete(`Tournament/${id}`);

export const getTournamentList = (
  pageNumber: number,
  pageSize: number,
  search?: string,
  sortBy?: "name" | "location" | "startDate" | "status",
  sortOrder?: "desc" | "asc",
  typeFilter?: string[],
  categoryFilter?: string[],
  statusFilter?: string[],
  locationFilter?: string[],
  publishedFilter?: string,
  month?: number,
  year?: number
): Promise<{ data: { items: ITournamentItem[]; pagination: IPagination } }> => {
  const params: Record<string, any> = {
    pageNumber,
    pageSize,
    sortBy: sortBy || "startDate",
    sortOrder: sortOrder || "asc",
  };

  if (search) params.search = search;
  if (typeFilter?.length) params.typeFilter = typeFilter;
  if (categoryFilter?.length) params.categoryFilter = categoryFilter;
  if (statusFilter?.length) params.statusFilter = statusFilter;
  if (locationFilter?.length) params.locationFilter = locationFilter;
  if (publishedFilter) params.publishedFilter = publishedFilter;
  if (month !== undefined) params.month = month;
  if (year !== undefined) params.year = year;

  return api.get("Tournament/all", {
    params,
    paramsSerializer: (p) => qs.stringify(p, { arrayFormat: "repeat" }),
  });
};

export const getTournamentDetail = (id: string) => api.get(`Tournament/${id}`);
export const getTournamentReport = (
  id: string
): Promise<{ data: import("../types/tournament").TournamentReportDTO }> => api.get(`Tournament/${id}/report`);

export const getTournamentsByDate = (
  onDate: string
): Promise<{ data: { id: string; name: string; startDate: string; endDate: string }[] }> =>
  api.get("Tournament/by-date", { params: { onDate } });
export const getTournamentCategoryDetail = (id: string) => api.get(`Tournament/${id}/detail`);
export const getTournamentCategoryWinners = (tournamentCategoryMappingId: string) =>
  api.get(`Tournament/category/${tournamentCategoryMappingId}/winners`);
export const updatePrizePaymentStatus = (request: UpdatePrizePaymentRequestDTO) =>
  api.put(`Tournament/prize-payment`, request);
export const getTournamentCategories = () => api.get("Tournament/categories");
export const getTournamentFormats = (id: string): Promise<{ data: ITournamentFormatResponse }> =>
  api.get(`Tournament/${id}/formats`);

export const getStageDetails = (tournamentId: string, stageId: string): Promise<IServiceResponse<StageDetails>> =>
  api.get(`Tournament/${tournamentId}/stage/${stageId}`);

export const addCategory = (id: string, category: AddTournamentCategoryDTO) =>
  api.post(`Tournament/${id}/category-details`, category);

export const editCategory = (id: string, category: EditTournamentCategoryDTO) =>
  api.put(`Tournament/category-details/${id}`, category);

export const deleteCategory = (id: string) => api.delete(`Tournament/category-details/${id}`);

export const getMatchById = (id: string): Promise<IServiceResponse<IMatchDetailDTO>> =>
  api.get(`Tournament/matches/${id}`);

export const updateMatch = (id: string, data: IEditMatchDTO) => api.put(`Tournament/matches/${id}`, data);

export const getPendingActions = (): Promise<{ data: { items: IPendingAction[]; pagination: IPagination } }> =>
  api.get(`Team/pending-actions?pageNumber=1&pageSize=20`);

export const updateTeamMemberStatus = (
  teamId: string,
  memberId: string,
  data: {
    status?: string;
    notes?: string;
    paymentStatus?: string;
    paymentMethod?: string | null;
    promoCode?: string | null;
    feeId?: string | null;
    playerName?: string;
  }
) => api.put(`Team/${teamId}/members/${memberId}`, data);

export const updateTeam = (
  teamId: string,
  data: {
    name?: string;
    promoCode?: string;
    teamMembers?: { teamMemberId?: string; playerId?: string; playerName?: string; consent?: boolean }[];
  }
) => api.put(`Team/${teamId}`, data);

export const deleteTeamMember = (id: string): Promise<{ data: boolean; message: string }> => api.delete(`Team/${id}`);

export const deleteTeam = (id: string): Promise<{ data: boolean; message: string }> => api.delete(`Team?teamId=${id}`);

export const reassignCaptain = (teamId: string, teamMemberId: string) =>
  api.put(`Team/${teamId}/reassign-captain`, { teamMemberId });

export const getTeamMatches = (teamId: string): Promise<{ data: ITeamMatch[] }> =>
  api.get(`Team/matches?teamId=${teamId}`);

export const getLeagueMatches = (
  formatId: string,
  filters?: { team: string; venue: string; round: string }
): Promise<IServiceResponse<{ matches: IMatchDetailDTO[]; filters: IMatchFilters }>> => {
  const params = new URLSearchParams();
  if (filters?.team) params.append("team", filters.team);
  if (filters?.venue) params.append("venue", filters.venue);
  if (filters?.round) params.append("round", filters.round);

  return api.get(`League/matches?formatId=${formatId}&${params}`);
};

export const previewLeagueMatches = (
  formatId: string,
  data: { teamIds: string[]; bestOf: number }
): Promise<IServiceResponse<IMatchDetailDTO[]>> => api.post(`League/preview?formatId=${formatId}`, data);

export const createLeagueMatches = (formatId: string, matches: IMatchDetailDTO[], teams: string[]) =>
  api.post(`League/matches?formatId=${formatId}`, { matches, teams });

export const deleteLeagueMatch = (formatId: string, id: string) =>
  api.delete(`League/matches/${id}?formatId=${formatId}`);

export const deleteLeagueSchedule = (formatId: string) => api.delete(`League/schedule?formatId=${formatId}`);

// Ranked by total points here, so every screen (stage table, champion, bracket seeding) reads the same order.
export const getLeagueStandings = (formatId: string): Promise<IServiceResponse<ILeagueStanding[]>> =>
  api
    .get(`League/standings?formatId=${formatId}`)
    .then((res: any) => ({ ...res, data: byTotalPoints(res?.data) }));

export const getGroupMatches = (
  formatId: string,
  filters?: { team: string; venue: string; round: string; group: string }
): Promise<IServiceResponse<{ matches: IMatchDetailDTO[]; filters: IMatchFilters }>> => {
  const params = new URLSearchParams();
  if (filters?.team) params.append("team", filters.team);
  if (filters?.venue) params.append("venue", filters.venue);
  if (filters?.round) params.append("round", filters.round);
  if (filters?.group) params.append("bracket", filters.group);

  return api.get(`Group/matches?formatId=${formatId}&${params}`);
};

export const previewGroupMatches = (
  formatId: string,
  data: {
    teamIds: string[];
    isRandom: boolean;
    numOfGroups: number;
    bestOf: number;
    groups?: { groupName: string; teamIds: string[] }[];
  }
): Promise<IServiceResponse<IMatchDetailDTO[]>> => api.post(`Group/preview?formatId=${formatId}`, data);

export const createGroupMatches = (
  formatId: string,
  matches: IMatchDetailDTO[],
  teams: { teamId: string; groupName: string }[]
) => api.post(`Group/matches?formatId=${formatId}`, { matches, teams });

export const deleteGroupMatch = (formatId: string, id: string) =>
  api.delete(`Group/matches/${id}?formatId=${formatId}`);

export const deleteGroupSchedule = (formatId: string) => api.delete(`Group/schedule?formatId=${formatId}`);

export const getGroupStandings = (formatId: string): Promise<IServiceResponse<IGroupStandingList[]>> =>
  api.get(`Group/standings?formatId=${formatId}`);

export const getKnockoutMatches = (
  formatId: string,
  filters?: { team: string; venue: string; round: string }
): Promise<IServiceResponse<{ matches: IMatchDetailDTO[]; filters: IMatchFilters }>> => {
  const params = new URLSearchParams();
  if (filters?.team) params.append("team", filters.team);
  if (filters?.venue) params.append("venue", filters.venue);
  if (filters?.round) params.append("round", filters.round);

  return api.get(`Knockout/matches?formatId=${formatId}&${params}`);
};

export const previewKnockoutMatches = (
  formatId: string,
  data: {
    teamIds: string[];
    bestOf: number;
    useSeeding: boolean;
    isRandom: boolean;
    teamgroups?: { [teamId: string]: string };
  }
): Promise<IServiceResponse<IMatchDetailDTO[]>> => api.post(`Knockout/preview?formatId=${formatId}`, data);

export const createKnockoutMatches = (formatId: string, matches: IMatchDetailDTO[]) =>
  api.post(`Knockout/matches?formatId=${formatId}`, matches);

export const updateKnockoutMatch = (id: string, data: IEditMatchDTO) => api.put(`Knockout/match/${id}`, data);

export const deleteKnockoutMatch = (formatId: string, id: string) =>
  api.delete(`Knockout/matches/${id}?formatId=${formatId}`);

export const deleteKnockoutSchedule = (formatId: string) => api.delete(`Knockout/schedule?formatId=${formatId}`);

// ── Americano / Mexicano ──
// Both controllers expose the same route set; clients pick the route by the stage's formatName.

export const previewAmericanoMatches = (
  formatId: string,
  data: { teamIds: string[] | null; courts?: number | null; rounds?: number | null }
): Promise<IServiceResponse<IMatchDetailDTO[]>> => api.post(`Americano/preview?formatId=${formatId}`, data);

export const createAmericanoMatches = (
  formatId: string,
  matches: IMatchDetailDTO[],
  teams: string[],
  opts?: { courts?: number | null; rounds?: number | null }
) => api.post(`Americano/matches?formatId=${formatId}`, { matches, teams, ...opts });

export const getAmericanoMatches = (formatId: string): Promise<IServiceResponse<IMatchDetailDTO[]>> =>
  api.get(`Americano/matches?formatId=${formatId}`);

export const getAmericanoStandings = (formatId: string): Promise<IServiceResponse<IAmericanoLeaderboardRow[]>> =>
  api.get(`Americano/standings?formatId=${formatId}`);

export const updateAmericanoMatch = (id: string, data: IEditMatchDTO) => api.put(`Americano/match/${id}`, data);

export const deleteAmericanoSchedule = (formatId: string) => api.delete(`Americano/schedule?formatId=${formatId}`);

// "Finish early": deletes every unscored match, keeps the scored ones (incl. finished courts of a
// half-played round) and lowers americanoRules.roundsCount to the rounds left, which ends the stage.
// Refused (400, message in error.message) while a match is still in progress or nothing is scored yet.
export const finishAmericano = (formatId: string): Promise<IServiceResponse<IAmericanoFinishResult>> =>
  api.post(`Americano/finish?formatId=${formatId}`);

// Mexicano runs live (spec 2026-09-29): the dashboard sets the stage up (players enrolled as
// Not here, courts + planned rounds); rounds are started from the referee portal.
export const setupMexicano = (
  formatId: string,
  data: { courts: number | null; rounds: number }
): Promise<IServiceResponse<IMexicanoSession>> => api.post(`Mexicano/setup?formatId=${formatId}`, data);

export const getMexicanoSession = (formatId: string): Promise<IServiceResponse<IMexicanoSession>> =>
  api.get(`Mexicano/session?formatId=${formatId}`);

export const getMexicanoMatches = (formatId: string): Promise<IServiceResponse<IMatchDetailDTO[]>> =>
  api.get(`Mexicano/matches?formatId=${formatId}`);

export const getMexicanoStandings = (formatId: string): Promise<IServiceResponse<IAmericanoLeaderboardRow[]>> =>
  api.get(`Mexicano/standings?formatId=${formatId}`);

export const updateMexicanoMatch = (id: string, data: IEditMatchDTO) => api.put(`Mexicano/match/${id}`, data);

export const deleteMexicanoSchedule = (formatId: string) => api.delete(`Mexicano/schedule?formatId=${formatId}`);


// Individual Registration APIs
export const registerIndividual = (
  tournamentCategoryMappingId: string,
  data: IndividualRegistrationDTO
): Promise<IServiceResponse<IndividualRegistrationResponse>> =>
  api.post(`Team/category/${tournamentCategoryMappingId}/individual-register`, data);

export const registerTeam = (data: {
  name: string;
  tournamentCategoryMappingId: string;
  promoCode?: string | null;
  useBalance?: boolean;
  payForTeam?: boolean;
  amount?: number;
  teamMembers: { playerId: string; feeId?: string | null; consent: boolean }[];
}) => api.post("Team", data);

export const getAvailableSpots = (
  tournamentCategoryMappingId: string
): Promise<IServiceResponse<AvailableSpotsDTO>> =>
  api.get(`Team/category/${tournamentCategoryMappingId}/available-spots`);

export const divideIndividuals = (data: any) => api.post("Team/divide-individuals", data);

export const movePlayerToTeam = (data: { teamMemberId: string; targetTeamId: string }) =>
  api.post("Team/move-player", data);
